import { useEffect, useRef, type RefObject } from 'react';
import gsap from 'gsap';
import styles from './wrong-answer-flash.module.css';

export interface WrongAnswerFlashProps {
  /** Increment to replay the effect. */
  trigger: number;
  shakeTargetRef?: RefObject<HTMLElement | null>;
}

/**
 * Brief full-screen red vignette pulse on wrong pedagogical answers.
 */
export function WrongAnswerFlash({ trigger, shakeTargetRef }: WrongAnswerFlashProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const flashRef = useRef<HTMLDivElement>(null);
  const vignetteRef = useRef<HTMLDivElement>(null);
  const streakRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (trigger <= 0) return;

    const overlay = overlayRef.current;
    const flash = flashRef.current;
    const vignette = vignetteRef.current;
    const streak = streakRef.current;
    const shakeTarget = shakeTargetRef?.current;
    if (!overlay || !flash || !vignette || !streak) return;

    gsap.set(overlay, { opacity: 1 });
    gsap.set(flash, { opacity: 0 });
    gsap.set(vignette, { opacity: 0 });
    gsap.set(streak, { opacity: 0, scaleX: 0.05, scaleY: 1 });

    const tl = gsap.timeline({
      onComplete: () => {
        gsap.set(overlay, { opacity: 0 });
      },
    });

    // Impact instantané
    tl.to(flash, { opacity: 0.7, duration: 0.035, ease: 'power4.in' }, 0)
      .to(vignette, { opacity: 1, duration: 0.04, ease: 'power4.in' }, 0)
      .to(streak, { opacity: 0.85, scaleX: 1, scaleY: 2.5, duration: 0.06, ease: 'power4.out' }, 0.01)
      .to(flash, { opacity: 0, duration: 0.14, ease: 'power3.out' }, 0.04)
      .to(streak, { opacity: 0, scaleY: 0.4, duration: 0.1, ease: 'power2.in' }, 0.07)
      .to(vignette, { opacity: 0, duration: 0.32, ease: 'power2.in' }, 0.1);

    if (shakeTarget) {
      gsap.fromTo(
        shakeTarget,
        { x: 0, y: 0, rotation: 0, scale: 1 },
        {
          duration: 0.38,
          ease: 'none',
          keyframes: [
            { x: -11, y: 3, rotation: -0.6, scale: 1.012, duration: 0.03 },
            { x: 13, y: -2, rotation: 0.7, scale: 1.008, duration: 0.035 },
            { x: -8, y: 2, rotation: -0.4, scale: 1.004, duration: 0.03 },
            { x: 5, y: -1, rotation: 0.25, scale: 1.002, duration: 0.025 },
            { x: 0, y: 0, rotation: 0, scale: 1, duration: 0.03 },
          ],
        },
        0
      );
    }

    return () => {
      tl.kill();
      gsap.killTweensOf([overlay, flash, vignette, streak, shakeTarget]);
      if (shakeTarget) gsap.set(shakeTarget, { x: 0, y: 0, rotation: 0, scale: 1 });
    };
  }, [trigger, shakeTargetRef]);

  if (trigger <= 0) return null;

  return (
    <div ref={overlayRef} className={styles.overlay} aria-hidden>
      <div ref={flashRef} className={styles.flash} />
      <div ref={vignetteRef} className={styles.vignette} />
      <div ref={streakRef} className={styles.streak} />
    </div>
  );
}
